'use client';

import { useCallback, useState, createContext, useRef } from 'react';
import { createUseGesture, dragAction } from '@use-gesture/react';
import useSWRInfinite from 'swr/infinite';
import { ClientApi } from './useApi';

const useGesture = createUseGesture([dragAction]);

const PullPageContext = createContext({} as any);

export const PullPageContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, setState] = useState(null);
  return (
    <PullPageContext.Provider value={{ state, setState }}>
      {children}
    </PullPageContext.Provider>
  );
};

export const usePullDownPage = (url: string) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { data, error, isLoading, mutate, setSize, size } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      return url || undefined;
    },
    ClientApi.get,
    {
      revalidateOnFocus: false, // 禁用页面聚焦时重新验证
      refreshWhenHidden: false, // 禁用页面隐藏时重新验证
      dedupingInterval: 2000, // 防止在短时间内重复请求
    }
  );

  const onMore = useCallback(() => {
    setSize((p) => p + 1);
  }, []);

  const bind = useGesture({
    onDrag({ movement: [, my], memo, cancel }) {
      // 如果滚动到底部或顶部，取消默认行为
      if (!scrollRef.current) return memo;
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (
        (scrollTop === 0 && my > 0) ||
        (scrollHeight - scrollTop === clientHeight && my < 0)
      ) {
        cancel();
      }
      return memo;
    },
    onDragEnd({
      movement: [, my],
      direction: [, dy],
      velocity: [, vy],
      memo,
      cancel,
    }) {
      if (!scrollRef.current) return memo;
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      // 上拉刷新
      if (scrollTop === 0 && dy < 0) {
        // 当用户向下拉动超过一定距离或速度达到阈值时触发刷新
        if (my > 50 || vy < -0.3) {
          // 取消默认行为
          cancel();
          mutate();
        }
        // 下拉加载
      } else if (scrollHeight - scrollTop === clientHeight && dy > 0) {
        // 当用户向上拉动超过一定距离或速度达到阈值时触发加载更多
        if (my < -50 || vy > 0.3) {
          // 取消默认行为
          cancel();
          onMore();
        }
      }
      return memo;
    },
  });

  return {
    onRefresh: mutate,
    onMore,
    bind,
    data,
    isLoading,
    error,
    size,
    scrollRef,
  };
};
